import m from '@bitstillery/mithril'
import {MithrilComponent} from '@bitstillery/mithril'
import {classes} from '@bitstillery/common/lib/utils'
import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'

/**
 * Styled in-app or external link. Self-contained: it never imports `$s` or `@bitstillery/common/app`,
 * because CMS transpiled views load this module via the catalog.
 */
export const linkMeta = {
    tier: 'inline',
    summary: 'Styled in-app or external link.',
    when_to_use:
        'Prefer over a raw anchor for portal styling. Router handles internal paths; http(s)/mailto/tel use a plain anchor.',
    example: `
    <Link href="/contact">Talk to our team</Link>
    `,
    variants: [
        {
            prop: 'disabled',
            jsx: `
            <Link disabled href="/contact">Talk to our team</Link>
            `,
        },
    ],
} satisfies CmsComponentMeta

export interface LinkAttrs {
    /** Internal route or http(s)/mailto/tel; the link text goes as children. */
    href: string
    /** Colour/theme (default `info`). */
    type?: 'info' | 'default' | 'brand' | 'success' | 'warning' | 'danger' | 'surface'
    className?: string
    /** e.g. `'_blank'`; external links default to a new tab. */
    target?: string | undefined
    disabled?: boolean
    tabindex?: number
    onclick?: (e: Event) => void
}

// Called through `m()`: its `ComponentType` admits a plain component object, which JSX can't take as a tag.
const RouteLink = m.route.Link

export class Link extends MithrilComponent<LinkAttrs> {
    view(vnode: m.Vnode<LinkAttrs>): m.Children {
        const a = vnode.attrs
        const href = encodeURI(a.href)
        const is_external = href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:')
        const link_class = classes('c-link', 'no-click', `type-${a.type ?? 'info'}`, a.className)

        if (is_external) {
            return (
                <a
                    className={link_class}
                    disabled={a.disabled}
                    href={href}
                    rel='noreferrer noopener'
                    target={a.target ?? '_blank'}
                >
                    {vnode.children}
                </a>
            )
        }

        if (a.disabled) {
            return (
                <a aria-disabled='true' className={link_class} role='link' tabindex={-1}>
                    {vnode.children}
                </a>
            )
        }

        return m(
            RouteLink,
            {
                className: link_class,
                href: href,
                onclick: (e: Event) => {
                    e.stopPropagation()
                    a.onclick?.(e)
                },
                rel: 'noreferrer noopener',
                tabindex: a.tabindex ?? -1,
                target: a.target ?? '_self',
            },
            vnode.children,
        )
    }
}
